import React, { useEffect, useState } from "react";
import paperUploadIcon from "@assets/icons/paper-upload.svg";
import ModalView from "@modals/modalview/ModalView";
import UploadValidation from "@modals/uploadvalidation/UploadValidation";
import ViewValidationErrors from "@modals/viewvalidationerrors/ViewValidationErrors";
import styles from "./UploadAnswerSheets.module.css";
import PDFUpload from "@components/pdfupload/PDFUpload";
import Loader from "@components/loader/Loader";
import {
  uploadFileToS3,
  createBulkUpload,
  createAnswersheetSubmission,
  getAnswersheetUploadURL,
} from "@services/answersheetUpload";
import { useSelector } from "react-redux";

function UploadAnswerSheets({
  isOpen,
  examSessionId,
  questionPaperId,
  onCompleteAnswerSheetUpload,
  onClose,
}: {
  isOpen: boolean;
  examSessionId: any;
  questionPaperId: any;
  onCompleteAnswerSheetUpload: (failedUploads?: any[]) => void;
  onClose: () => void;
}) {
  const studentUploadSessionData = useSelector(
    (state: any) => state.adminDashboardData.sessionStudentUploadData
  );
  const [isLoading, setIsLoading] = useState(false);
  const userDto = JSON.parse(localStorage.getItem("userDto") || "{}");
  const [studentFileDetails, setStudentFileDetails] = useState<any[]>([]);
  const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
  const [uploadStatus, setUploadStatus] = useState<
    "idle" | "uploading" | "completed"
  >("idle");
  const [pdfFilesUploadStatus, setPdfFilesUploadStatus] = useState<
    | "idle"
    | "errorDetected"
    | "displayError"
    | "completed"
    | "bulkUploadCompleted"
    | ""
  >("idle");
  const [errorsDetected, setErrorsDetected] = useState<string[]>([]);
  const [files, setFiles] = useState<any[]>([]);
  const [maxFiles, setMaxFiles] = useState<number>(0);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [failedUploadsState, setFailedUploadsState] = useState<any[]>([]);

  const handleFiles = async (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;
    const fileArray = Array.from(selectedFiles);

    const errors: string[] = [];
    const MAX_FILE_SIZE = 100 * 1024 * 1024;

    const pdfFiles = fileArray.filter(
      (file) => file.type === "application/pdf"
    );
    if (pdfFiles.length !== fileArray.length) {
      fileArray
        .filter((file) => file.type !== "application/pdf")
        .forEach((file) => errors.push(`${file.name} is not a PDF file.`));
    }

    if (pdfFiles.length + files.length > maxFiles) {
      errors.push(`You can only upload up to ${maxFiles} files.`);
    }

    const studentMap = new Map(
      studentFileDetails.map((s: any) => [
        s.pdf_file_name,
        s.student_identifier,
      ])
    );

    pdfFiles.forEach((file) => {
      if (!studentMap.has(file.name)) {
        errors.push(`${file.name} does not match any student record.`);
      }
    });

    pdfFiles.forEach((file) => {
      if (file.size > MAX_FILE_SIZE) {
        errors.push(`${file.name} exceeds the 100 MB size limit.`);
      }
    });

    if (errors.length > 0) {
      console.error("Validation Errors:", errors);
      setPdfFilesUploadStatus("errorDetected");
      setErrorsDetected(errors);
      return;
    }

    const newFiles = pdfFiles.map((file) => ({
      original_filename: file.name,
      file,
      student_identifier: studentMap.get(file.name)!,
      uploadStatus: "pending",
    }));
    console.log("fileList: ", newFiles);
    setFiles((prev) => [...prev, ...newFiles]);
    setUploadStatus("uploading");
    setUploadProgress(0);

    const totalFiles = newFiles.length;
    let successfulUploads = 0;
    Promise.allSettled(
      newFiles.map((file) =>
        getAnswersheetUploadURL({
          filename: `${examSessionId}_${file.student_identifier}.pdf`,
        })
          .then((response: any) =>
            uploadFileToS3(file.file, response.upload_url)
          )
          .then(() => {
            successfulUploads++;
            setUploadProgress(
              Math.round((successfulUploads / totalFiles) * 100)
            );
            setFiles((prev) =>
              prev.map((f) =>
                f.original_filename === file.original_filename
                  ? {
                    ...f,
                    uploadStatus: "success",
                    filename: `${examSessionId}_${file.student_identifier}.pdf`,
                  }
                  : f
              )
            );
          })
          .catch((error) => {
            console.error(`Failed to upload ${file.original_filename}`, error);
            setFiles((prev) =>
              prev.map((f) =>
                f.original_filename === file.original_filename
                  ? { ...f, uploadStatus: "failed" }
                  : f
              )
            );
          })
      )
    ).then(() => {
      setUploadStatus("completed");
    });
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handleBulkUpload = async () => {
    setIsLoading(true);
    const successfulUploads = files?.filter(
      (file) => file.uploadStatus == "success"
    );

    const failedUploads: any[] = files
      ?.filter((file) => file.uploadStatus === "failed")
      .map((file) => ({
        student_id: file.student_identifier,
        filename: file.original_filename,
        reason: "S3 Upload Failed",
      }));

    try {
      const results = await Promise.allSettled(
        successfulUploads.map(
          async ({ student_identifier, file, filename }) => {
            const payload = {
              exam_session_id: examSessionId,
              submitted_by: student_identifier,
              question_paper_submissions_documents: [
                {
                  question_paper_id: questionPaperId,
                  document: {
                    filename: filename,
                  },
                },
              ],
            };
            return await createAnswersheetSubmission(payload);
          }
        )
      );

      results.forEach((result, index) => {
        if (result.status === "rejected") {
          const errorMessage =
            result.reason?.message || "API Submission Failed";
          failedUploads.push({
            student_id: successfulUploads[index].student_identifier,
            filename: successfulUploads[index].original_filename,
            reason: errorMessage,
          });
        }
      });

      setFailedUploadsState(failedUploads);
      setPdfFilesUploadStatus("bulkUploadCompleted");

      // Pass failed uploads to the completion handler, but we need to wait for user to click OK in "bulkUploadCompleted" view
    } catch (error) {
      console.error("Error during bulk upload:", error);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (isOpen) {
      try {
        const fileList = studentUploadSessionData || [];
        if (fileList.length > 0) {
          setMaxFiles(fileList.length);
          setStudentFileDetails(fileList);
          setIsDataLoaded(true);
        } else {
          console.warn("No student file details found in localStorage");
          setIsDataLoaded(true);
        }
      } catch (error) {
        console.error(
          "Error parsing studentFileDetails from localStorage:",
          error
        );
        setIsDataLoaded(true);
      }
    }
  }, [isOpen]);

  if (isLoading) {
    return <Loader />;
  }

  const renderUploadContent = () => {
    switch (pdfFilesUploadStatus) {
      case "idle":
        return (
          <ModalView isOpen={isOpen} onClose={onClose}>
            <div className={styles.uploadAnswerSheetsContainer}>
              <div className={styles.header}>
                <h2>Upload Answer Sheets</h2>
              </div>
              <div className={styles.content}>
                <div
                  className={styles.fileUploadContainer}
                  onDrop={handleDrop}
                  onDragOver={(e) => e.preventDefault()}
                >
                  {uploadStatus === "idle" && (
                    <div className={styles.fileUploadInputContainer}>
                      <img
                        src={paperUploadIcon}
                        alt="Upload"
                        className={styles.paperUploadIcon}
                      />
                      <h2 className={styles.dragAndDropText}>
                        Drag or Attach Multiple Answer Sheets
                      </h2>
                      <p className={styles.dragAndDropSubText}>
                        Please select pdf files of max size upto 100 MB each
                        (Supported file type: pdf)
                      </p>
                      <form className={styles.form}>
                        <input
                          type="file"
                          id="attachPdfFiles"
                          className={styles.fileInput}
                          accept="application/pdf"
                          multiple
                          onChange={(e) => handleFiles(e.target.files)}
                        />
                        <label
                          htmlFor="attachPdfFiles"
                          className={styles.fileLabel}
                        >
                          Attach PDF Files
                        </label>
                      </form>
                    </div>
                  )}

                  {uploadStatus !== "idle" && (
                    <div className={styles.uploadContainer}>
                      <div className={styles.filesUploadingContainer}>
                        <p className={styles.uploadingFilesCount}>
                          Attaching {files.length} Answer Sheets
                        </p>
                        <div className={styles.uploadingProgress}>
                          <div
                            className={styles.progressBar}
                            style={{ width: `${uploadProgress}%` }}
                          ></div>
                        </div>
                        <p className={styles.uploadingPercentage}>
                          {uploadProgress}%
                        </p>
                        <button
                          className={styles.uploadCancelButton}
                          onClick={() => onClose()}
                        >
                          Cancel
                        </button>
                      </div>

                      <div className={styles.uploadingList}>
                        {files.map((file) => (
                          <PDFUpload
                            key={file.original_filename}
                            fileData={{
                              fileName: file.original_filename,
                              admissionId: file.student_identifier,
                              uploadStatus: file.uploadStatus,
                            }}
                          />
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className={styles.buttonContainer}>
                <button className={styles.cancelButton} onClick={onClose}>
                  Cancel
                </button>
                <button
                  className={styles.continueButton}
                  disabled={uploadStatus != "completed"}
                  onClick={handleBulkUpload}
                >
                  Save
                </button>
              </div>
            </div>
          </ModalView>
        );
      case "errorDetected":
        return (
          <UploadValidation
            isOpen={true}
            errorsDetected={errorsDetected || []}
            handleOk={() => setPdfFilesUploadStatus("displayError")}
            onClose={() => setPdfFilesUploadStatus("idle")}
          />
        );

      case "displayError":
        return (
          <ViewValidationErrors
            isOpen={true}
            handleReupload={() => setPdfFilesUploadStatus("idle")}
            errorsDetected={errorsDetected || []}
            onClose={() => setPdfFilesUploadStatus("idle")}
          />
        );

      case "bulkUploadCompleted":
        return (
          <UploadValidation
            isOpen={true}
            errorsDetected={[]}
            handleOk={() => {
              onCompleteAnswerSheetUpload(failedUploadsState);
            }}
            onClose={() => { }}
          />
        );

      default:
        return null;
    }
  };

  return <>{renderUploadContent()}</>;
}

export default UploadAnswerSheets;
